// Updated SignInPage component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { ReactComponent as Logo } from "../../assects/svg/auth/Triva_Logo.svg";
import { ReactComponent as Trivaillusion } from "../../assects/svg/auth/Triva_illusion.svg";
import { useAuth } from '../../context/auth/AuthContext';
import { signin } from '../../services/auth';
import { ApiError } from '../../services/http';
import { Button } from '../Ui_Components/Button';

const SignInPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaVerifying, setCaptchaVerifying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [nutritionistId, setNutritionistId] = useState<number | null>(null);
  
  // Validation states
  const [emailError, setEmailError] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [apiError, setApiError] = useState('');

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Email validation
  const validateEmail = (value: string): string => {
    if (!value) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setApiError('');
    if (emailTouched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  const handleSubmit = () => {
    const emailErr = validateEmail(email);
    
    setEmailError(emailErr);
    setEmailTouched(true);

    if (!emailErr && captchaVerified && nutritionistId) {
      console.log('Proceeding to 2FA with:', { email, nutritionistId });
      navigate('/signin/verify-otp', { 
        state: { 
          email,
          nutritionistId 
        } 
      });
    }
  };

  const handleCaptchaClick = async () => {
    if (captchaVerifying || captchaVerified) return;
    
    const emailErr = validateEmail(email);
    
    setEmailTouched(true);
    setEmailError(emailErr);
    setApiError('');

    if (emailErr) {
      return;
    }
    
    setCaptchaVerifying(true);
    setProgress(0);
    
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    try {
      const { nutritionistId: id } = await signin(email);

      clearInterval(progressInterval);
      setProgress(100);

      setTimeout(() => {
        setCaptchaVerifying(false);
        setCaptchaVerified(true);
        setNutritionistId(id);
      }, 300);
    } catch (error) {
      clearInterval(progressInterval);
      setProgress(0);
      setCaptchaVerifying(false);
      if (error instanceof ApiError) {
        setApiError(error.message);
      } else {
        setApiError('Network error. Please check your connection and try again.');
      }
      console.error('Sign-in error:', error);
    }
  };

  const isFormValid = !emailError && email && captchaVerified;

  return (
    <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center p-4 relative">
      <div className="absolute top-8 right-8">
        <div className="flex items-center gap-2">
         <Logo className="h-10 w-auto" />
        </div>
      </div>

      <div className="absolute bottom-8 left-8">
         <Trivaillusion className="h-36 w-auto" />
      </div>

      <div className="w-full max-w-[520px] bg-white rounded-xl border-[0.5px] border-[#2F2E38] shadow-sm p-6 sm:p-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">
          Team panel login
        </h1>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-normal text-gray-700 mb-2">
              Enter registered email
            </label>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={handleEmailBlur}
              className={`w-full px-4 py-3 bg-white border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                emailError && emailTouched
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-300 focus:ring-teal-400'
              }`}
              placeholder="youremail@mail.com"
            />
            {emailError && emailTouched && (
              <p className="mt-1.5 text-xs text-red-500">{emailError}</p>
            )}
          </div>

          <div>
            <button
              type="button"
              onClick={handleCaptchaClick}
              disabled={captchaVerifying || captchaVerified}
              className={`w-full relative overflow-hidden px-4 py-3 border border-l-4 rounded-lg transition-all ${
                captchaVerified
                  ? 'border-l-teal-500 bg-teal-50 border-teal-200'
                  : captchaVerifying
                  ? 'border-l-teal-400 bg-gray-50 border-[#CCCCCC]'
                  : 'border-l-teal-400 border-[#CCCCCC] hover:border-gray-400'
              } ${captchaVerifying || captchaVerified ? 'cursor-default' : 'cursor-pointer'}`}
              style={!captchaVerified && !captchaVerifying ? {
                background: 'linear-gradient(180deg, #FFFFFF 0%, #F4F4F4 100%)'
              } : {}}
            >
              {captchaVerifying && (
                <div 
                  className="absolute inset-0 bg-teal-400 opacity-20 transition-all duration-100 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              )}
              
              <div className="relative flex items-center justify-between">
                <span className={`text-sm font-medium ${
                  captchaVerified 
                    ? 'text-teal-700' 
                    : captchaVerifying 
                    ? 'text-gray-700' 
                    : 'text-gray-700'
                }`}>
                  {captchaVerified ? '✓ Verified' : 'Click to verify'}
                </span>
                <RefreshCw className={`w-5 h-5 ${
                  captchaVerified 
                    ? 'text-teal-600' 
                    : captchaVerifying 
                    ? 'text-gray-500 animate-spin' 
                    : 'text-gray-500'
                }`} />
              </div>
            </button>
            {apiError && (
              <p className="mt-1.5 text-xs text-red-500">{apiError}</p>
            )}
          </div>

          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleSubmit}
            disabled={!isFormValid}
            className="mt-6"
          >
            Sign in
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignInPage;